import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Maps ESPN team IDs → division names for NFL
const NFL_DIV_MAP = {
  2: 'AFC East', 15: 'AFC East', 17: 'AFC East', 20: 'AFC East',
  33: 'AFC North', 4: 'AFC North', 5: 'AFC North', 23: 'AFC North',
  34: 'AFC South', 11: 'AFC South', 30: 'AFC South', 10: 'AFC South',
  7: 'AFC West', 12: 'AFC West', 13: 'AFC West', 24: 'AFC West',
  6: 'NFC East', 19: 'NFC East', 21: 'NFC East', 28: 'NFC East',
  3: 'NFC North', 8: 'NFC North', 9: 'NFC North', 16: 'NFC North',
  1: 'NFC South', 29: 'NFC South', 18: 'NFC South', 27: 'NFC South',
  22: 'NFC West', 14: 'NFC West', 26: 'NFC West', 25: 'NFC West',
};

const NFL_CONF_DIVISIONS = {
  AFC: ['AFC East', 'AFC North', 'AFC South', 'AFC West'],
  NFC: ['NFC East', 'NFC North', 'NFC South', 'NFC West'],
};

let _nflCache = null;

export function NFLCompactStandings() {
  const [byDiv, setByDiv] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (_nflCache) { setByDiv(_nflCache); setLoading(false); return; }
    axios.get('https://site.api.espn.com/apis/v2/sports/football/nfl/standings', { timeout: 8000 })
      .then(r => {
        const teams = {};
        for (const conf of (r.data?.children || [])) {
          for (const entry of (conf?.standings?.entries || [])) {
            const t = entry.team;
            const stats = Object.fromEntries((entry.stats || []).map(s => [s.name, s.value]));
            const id = Number(t.id);
            teams[id] = {
              id, abbr: t.abbreviation,
              name: t.shortDisplayName || t.name,
              logo: t.logos?.[0]?.href,
              wins: stats.wins ?? 0, losses: stats.losses ?? 0, ties: stats.ties ?? 0,
              pct: stats.winPercent ?? 0,
              div: NFL_DIV_MAP[id],
            };
          }
        }
        const grouped = {};
        for (const t of Object.values(teams)) {
          if (!grouped[t.div]) grouped[t.div] = [];
          grouped[t.div].push(t);
        }
        for (const div of Object.keys(grouped)) {
          grouped[div].sort((a, b) => b.pct - a.pct || a.losses - b.losses);
        }
        _nflCache = grouped;
        setByDiv(grouped);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} color="primary" /></Box>;

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#9eb8d0', mb: 1.5, letterSpacing: '0.06em' }}>
        📊 STANDINGS
      </Typography>
      {Object.entries(NFL_CONF_DIVISIONS).map(([conf, divs]) => (
        <Box key={conf} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#f5a623', letterSpacing: '0.08em', mb: 0.75 }}>
            {conf}
          </Typography>
          {divs.map(divName => {
            const teams = byDiv[divName] || [];
            if (!teams.length) return null;
            return (
              <Box key={divName} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#3a5070', letterSpacing: '0.06em', mb: 0.4 }}>
                  {divName.replace(/AFC |NFC /, '')}
                </Typography>
                {teams.map((t, i) => (
                  <Box key={t.id} onClick={() => navigate(`/team/${t.id}`)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.3, px: 0.5, borderRadius: 1, cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(46,168,75,0.08)' },
                      borderLeft: i === 0 ? '2px solid #2ea84b44' : '2px solid transparent',
                    }}>
                    {t.logo && <Avatar src={t.logo} sx={{ width: 16, height: 16, borderRadius: '2px' }} />}
                    <Typography sx={{ fontSize: '0.72rem', color: i === 0 ? '#e8f0f8' : '#9eb8d0', fontWeight: i === 0 ? 700 : 400, flex: 1, lineHeight: 1.2 }}>
                      {t.abbr}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#6a8aa8', fontVariantNumeric: 'tabular-nums' }}>
                      {t.wins}-{t.losses}{t.ties > 0 ? `-${t.ties}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

let _cfbCache = null;

// Top CFB conferences to show in standings sidebar
const CFB_CONF_IDS = [
  { id: 9, name: 'SEC' }, { id: 8, name: 'Big 10' }, { id: 4, name: 'Big 12' },
  { id: 1, name: 'ACC' }, { id: 23, name: 'AAC' },
];

export function CollegeCompactStandings() {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (_cfbCache) { setStandings(_cfbCache); setLoading(false); return; }
    Promise.all(
      CFB_CONF_IDS.map(c =>
        axios.get(`https://site.api.espn.com/apis/site/v2/sports/football/college-football/standings?group=${c.id}`, { timeout: 8000 })
          .then(r => {
            const entries = [];
            for (const div of (r.data?.children || [r.data])) {
              for (const entry of (div?.standings?.entries || [])) {
                const t = entry.team;
                const stats = Object.fromEntries((entry.stats || []).map(s => [s.name, s.value]));
                entries.push({
                  id: t.id, abbr: t.abbreviation,
                  name: t.shortDisplayName || t.name,
                  logo: t.logos?.[0]?.href,
                  wins: stats.wins ?? 0, losses: stats.losses ?? 0,
                  pct: stats.winPercent ?? 0,
                  confName: c.name,
                });
              }
            }
            return { conf: c.name, teams: entries.sort((a, b) => b.pct - a.pct || a.losses - b.losses).slice(0, 6) };
          }).catch(() => ({ conf: c.name, teams: [] }))
      )
    ).then(results => {
      _cfbCache = results;
      setStandings(results);
      setLoading(false);
    });
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} color="primary" /></Box>;

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#9eb8d0', mb: 1.5, letterSpacing: '0.06em' }}>
        📊 STANDINGS
      </Typography>
      {standings.map(({ conf, teams }) => {
        if (!teams.length) return null;
        return (
          <Box key={conf} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#f5a623', letterSpacing: '0.08em', mb: 0.75 }}>
              {conf}
            </Typography>
            {teams.map((t, i) => (
              <Box key={t.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.3, px: 0.5, borderRadius: 1,
                  borderLeft: i === 0 ? '2px solid #2ea84b44' : '2px solid transparent' }}>
                {t.logo && <Avatar src={t.logo} sx={{ width: 16, height: 16, borderRadius: '2px' }} onError={e => { e.target.style.display = 'none'; }} />}
                <Typography sx={{ fontSize: '0.72rem', color: i === 0 ? '#e8f0f8' : '#9eb8d0', fontWeight: i === 0 ? 700 : 400, flex: 1, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.name}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#6a8aa8', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {t.wins}-{t.losses}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
